import { useMemo } from 'react';
import { LineKeyType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { useQuery } from 'react-query';
import { LineKeysApi } from '@frontend/api-line-keys';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { genUUIDV4 } from '@frontend/string';
import { ModalControlTriggerProps } from '@frontend/design-system';
import { useLineKeyContext, useLineKeyState } from '../store/line-key-provider';

type Props = {
  openUserExtensionsModal: ModalControlTriggerProps['onClick'];
  openContactsModal: ModalControlTriggerProps['onClick'];
};

export type ConfigItem = {
  title: string;
  subtitle: string;
  disabled?: boolean;
  onClick: () => void;
};

export const useLineKeyMenuConfig = ({ openUserExtensionsModal, openContactsModal }: Props) => {
  const { lineKeys } = useLineKeyContext();
  const { addNewKey, finalLineKeys } = useLineKeyState(['addNewKey', 'finalLineKeys']);
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const { t } = useTranslation('phone');

  const existingParkSlotIndexes = useMemo(() => {
    return (
      Object.values(finalLineKeys ?? lineKeys ?? {})?.reduce(
        (acc, lineKey) => ({
          ...acc,
          [lineKey.parkSlotNumber > 0 ? lineKey.parkSlotNumber : 'empty']: true,
        }),
        {} as Record<number, boolean>
      ) ?? {}
    );
  }, [finalLineKeys]);

  const { data: lineKeyOptions } = useQuery({
    queryKey: [tenantId, 'line-keys', 'line-key-options'],
    queryFn: () =>
      LineKeysApi.GetLineKeyOptions({
        tenantId,
      }),
    meta: {
      error: {
        message: 'useLineKeyMenuConfig:lineKeyOptionsQuery | Failed to fetch line key options',
        data: { tenantId },
      },
    },
  });

  const config: ConfigItem[] = [
    {
      title: t('Hold'),
      subtitle: t('Universal hold slots, also known as park'),
      disabled: false,
      onClick: () => {
        const sortedIndexs = Object.keys(existingParkSlotIndexes)
          .slice()
          .sort((a, b) => {
            return +a - +b;
          });
        let nextNumber = 1;
        for (const num of sortedIndexs) {
          if (+num === nextNumber) {
            nextNumber++;
          } else {
            break;
          }
        }
        addNewKey({
          [genUUIDV4()]: {
            lineKeyType: LineKeyType_Enum.PARK_SLOT,
            name: `Hold ${nextNumber}`,
            parkSlotNumber: nextNumber,
            contactId: '',
            extensionId: '',
            presenceEnabled: false,
            intercomEnabled: false,
            dataEndpointId: '',
          },
        });
      },
    },
    {
      title: t('Device Extension'),
      subtitle: t('Speed dial Device Extensions with options to monitor activity or intercom'),
      onClick: openUserExtensionsModal,
    },
    {
      title: t('Office Extension'),
      subtitle: t('Speed dial office extensions'),
      disabled: !lineKeyOptions?.officeExtensions.length,
      onClick: () => {
        addNewKey({
          [genUUIDV4()]: {
            lineKeyType: LineKeyType_Enum.OFFICE_EXTENSION,
            name: `${lineKeyOptions?.officeExtensions?.[0]?.name}`,
            extensionId: `${lineKeyOptions?.officeExtensions?.[0]?.extensionId}`,
            contactId: '',
            presenceEnabled: false,
            intercomEnabled: false,
            dataEndpointId: '',
            parkSlotNumber: 0,
          },
        });
      },
    },
    {
      title: t('Voicemail'),
      subtitle: t("Check your office's voicemail boxes"),
      disabled: !lineKeyOptions?.voicemailExtensions.length,
      onClick: () => {
        addNewKey({
          [genUUIDV4()]: {
            lineKeyType: LineKeyType_Enum.VOICEMAIL_EXTENSION,
            name: `${lineKeyOptions?.voicemailExtensions?.[0].name} ${lineKeyOptions?.voicemailExtensions?.[0].extensionNumber}`,
            extensionId: `${lineKeyOptions?.voicemailExtensions?.[0].extensionId}`,
            contactId: '',
            parkSlotNumber: 0,
            presenceEnabled: false,
            intercomEnabled: false,
            dataEndpointId: '',
          },
        });
      },
    },
    {
      title: t('Contact'),
      subtitle: t('Speed dial contacts in your local contact directory'),
      onClick: openContactsModal,
    },
  ];
  return {
    config,
  };
};
